/* =========================================================
   MIMAVEIRI — THEME JS
   Custom controls. No Shopify Dawn web-components.
   Minimal, dependency-free.
   ========================================================= */

(function () {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* ---------- Drawer system (lazy, defensive) ---------- */
  const wiredDrawers = new Set();

  function getDrawer(id) {
    return document.getElementById(id);
  }

  function wireDrawer(id) {
    if (wiredDrawers.has(id)) return;
    const el = getDrawer(id);
    if (!el) return;
    wiredDrawers.add(id);
    el.addEventListener('click', (e) => {
      if (e.target.closest('[data-drawer-close]') || e.target.classList.contains('drawer__backdrop')) {
        closeDrawer(id);
      }
    });
  }

  function openDrawer(id) {
    wireDrawer(id);
    const el = getDrawer(id);
    if (!el) return;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.body.classList.add('is-locked');
    const focusable = el.querySelector('input, button, [tabindex]');
    if (focusable) setTimeout(() => focusable.focus(), 250);
  }

  function closeDrawer(id) {
    const el = getDrawer(id);
    if (!el) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    const anyOpen = document.querySelector('.drawer.is-open, .search-overlay.is-open');
    if (!anyOpen) document.body.classList.remove('is-locked');
  }

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      document.querySelectorAll('.drawer.is-open').forEach(el => closeDrawer(el.id));
    }
  });

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-drawer-open]');
    if (!trigger) return;
    e.preventDefault();
    openDrawer(trigger.getAttribute('data-drawer-open'));
  });

  /* ---------- Cart ---------- */
  const Cart = {
    async fetch() {
      const r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return r.json();
    },
    async add(id, qty = 1) {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: qty }] })
      });
      return r.json();
    },
    async change(line, qty) {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: qty })
      });
      return r.json();
    },
    money(cents, format) {
      const n = (cents / 100).toFixed(2);
      if (!format) return '€ ' + n.replace('.', ',');
      return format.replace(/\{\{\s*amount(_with_comma_separator)?\s*\}\}/, n.replace('.', ','))
                   .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    }
  };
  window.Cart = Cart;

  async function refreshCartDrawer() {
    const html = await fetch('/?section_id=cart-drawer').then(r => r.text()).catch(() => null);
    if (!html) return;
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const fresh = tmp.querySelector('#cart-drawer');
    const current = document.getElementById('cart-drawer');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
    }
    const cart = await Cart.fetch();
    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = cart.item_count > 0 ? `(${cart.item_count})` : '';
      el.setAttribute('data-count', cart.item_count);
    });
  }
  window.refreshCartDrawer = refreshCartDrawer;

  /* ---------- Add to cart from PDP form ---------- */
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('[data-product-form]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const original = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.innerHTML = '…'; }
    const formData = new FormData(form);
    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });
      if (!res.ok) throw new Error('add failed');
      openDrawer('cart-drawer');
      await refreshCartDrawer();
    } catch (err) {
      console.error(err);
    } finally {
      if (btn) { btn.disabled = false; btn.innerHTML = original; }
    }
  });

  /* ---------- Cart line changes (drawer + page) ---------- */
  document.addEventListener('click', async (e) => {
    const stepper = e.target.closest('[data-qty-step]');
    if (stepper) {
      e.preventDefault();
      const wrap = stepper.closest('[data-line]');
      const input = wrap ? wrap.querySelector('input') : stepper.parentElement.querySelector('input');
      if (!input) return;
      const dir = parseInt(stepper.getAttribute('data-qty-step'), 10);
      const next = Math.max(0, parseInt(input.value, 10) + dir);
      input.value = next;
      if (wrap && wrap.dataset.line) {
        await Cart.change(parseInt(wrap.dataset.line, 10), next);
        await refreshCartDrawer();
        if (document.querySelector('.cart-page')) location.reload();
      }
    }

    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      const line = parseInt(remove.getAttribute('data-cart-remove'), 10);
      await Cart.change(line, 0);
      await refreshCartDrawer();
      if (document.querySelector('.cart-page')) location.reload();
    }
  });

  /* ---------- Variant selection ---------- */
  function initVariantPicker(root) {
    const data = root.querySelector('[data-variants-json]');
    if (!data) return;
    let variants;
    try { variants = JSON.parse(data.textContent); } catch { return; }
    const optionGroups = root.querySelectorAll('[data-option-group]');
    const variantInput = root.querySelector('[data-variant-input]');
    const priceEl = root.querySelector('[data-variant-price]');
    const submitEl = root.querySelector('[data-variant-submit]');

    function selected() {
      const opts = [];
      optionGroups.forEach(g => {
        const active = g.querySelector('[aria-pressed="true"]');
        if (active) opts.push(active.getAttribute('data-value'));
      });
      return opts;
    }

    function update() {
      const opts = selected();
      const v = variants.find(x =>
        (x.option1 || '') === (opts[0] || '') &&
        (x.option2 || '') === (opts[1] || '') &&
        (x.option3 || '') === (opts[2] || '')
      );
      if (!v) return;
      if (variantInput) variantInput.value = v.id;
      if (priceEl) priceEl.textContent = Cart.money(v.price, window.shop_money_format);
      if (submitEl) {
        submitEl.disabled = !v.available;
        const label = submitEl.querySelector('[data-cta-label]');
        if (label) {
          label.textContent = v.available ? submitEl.dataset.labelAvailable : submitEl.dataset.labelSoldout;
        }
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', v.id);
      window.history.replaceState({}, '', url);
    }

    optionGroups.forEach(group => {
      group.addEventListener('click', (e) => {
        const pill = e.target.closest('.variant-pill');
        if (!pill || pill.disabled) return;
        group.querySelectorAll('.variant-pill').forEach(p => p.setAttribute('aria-pressed', 'false'));
        pill.setAttribute('aria-pressed', 'true');
        update();
      });
    });

    update();
  }

  document.querySelectorAll('[data-variant-picker]').forEach(root => {
    try { initVariantPicker(root); } catch (err) { console.warn('Variant picker init failed', err); }
  });

  /* ---------- Reveal on scroll ---------- */
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach(en => {
        if (en.isIntersecting) {
          en.target.classList.add('is-revealed');
          io.unobserve(en.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('[data-reveal]').forEach(el => io.observe(el));
  } else {
    document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-revealed'));
  }

  /* ---------- Search overlay ---------- */
  const searchOverlay = document.getElementById('search-overlay');
  if (searchOverlay) {
    document.querySelectorAll('[data-search-open]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        searchOverlay.classList.add('is-open');
        document.body.classList.add('is-locked');
        const input = searchOverlay.querySelector('input');
        setTimeout(() => input && input.focus(), 50);
      });
    });
    searchOverlay.querySelectorAll('[data-search-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        searchOverlay.classList.remove('is-open');
        document.body.classList.remove('is-locked');
      });
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && searchOverlay.classList.contains('is-open')) {
        searchOverlay.classList.remove('is-open');
        document.body.classList.remove('is-locked');
      }
    });
  }

  /* ---------- Pre-wire drawers if present ---------- */
  ['cart-drawer', 'mobile-nav', 'filter-drawer'].forEach(wireDrawer);

  /* ---------- Newsletter inline UX ---------- */
  document.querySelectorAll('[data-newsletter-form]').forEach(form => {
    form.addEventListener('submit', () => {
      // Shopify customer endpoint posts natively; we only show success message via URL hash on return
    });
  });
  if (window.location.hash === '#newsletter-success') {
    document.querySelectorAll('[data-newsletter-success]').forEach(el => el.style.display = 'block');
  }

  /* ---------- Header scrolled state (subtle) ---------- */
  const header = document.querySelector('.site-header');
  if (header) {
    let last = 0;
    window.addEventListener('scroll', () => {
      const y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 8);
      last = y;
    }, { passive: true });
  }

})();
